import { useState } from 'react';
import { Link } from 'react-router-dom';

// FAQ items structured with optional article link for cross-linking
const faqs: {
  question: string;
  answer: string;
  articleLink?: { href: string; text: string };
}[] = [
  {
    question: 'How much life insurance does a family in Indianapolis actually need?',
    answer: 'Most Indianapolis families need coverage equal to 10-12 times their annual income. For example, if you earn $50,000 per year, you should consider $500,000-$600,000 in term life insurance coverage. This ensures your family can maintain their lifestyle, pay off the mortgage, and cover education costs if something happens to you. Our complimentary Financial Needs Analysis helps Marion County families determine their exact coverage needs based on their unique situation.',
    articleLink: {
      href: '/blog/how-much-life-insurance-indiana',
      text: 'Deep dive: How Much Life Insurance Do I Need in Indiana? (DIME method + real 2025 rates)',
    },
  },
  {
    question: 'What is the difference between Term and Whole Life insurance?',
    answer: 'Term life insurance provides coverage for a specific period (like 20 or 30 years) and is much more affordable, making it ideal for middle-income Indianapolis families. Whole life insurance lasts your entire lifetime and includes a savings component, but costs significantly more. For most Carmel, Fishers, and Greenwood families, term life insurance offers the best value - more coverage for less money when you need it most.',
    articleLink: {
      href: '/blog/term-vs-whole-life-indianapolis',
      text: 'Full comparison: Term vs. Whole Life Insurance for Indianapolis Homeowners',
    },
  },
  {
    question: 'How quickly can I get Indianapolis life insurance quotes?',
    answer: "You can receive personalized life insurance quotes for Indianapolis residents within 24 hours. Simply fill out our quick form above or call us at (317) 648-2983. We'll review your needs, explain your options, and provide transparent pricing with no hidden fees. Many Central Indiana families are surprised to learn how affordable term life insurance can be.",
    articleLink: {
      href: '/blog/life-insurance-quotes-indiana',
      text: 'Guide: Life Insurance Quotes in Indiana — What Affects Your Rate & How to Save',
    },
  },
  {
    question: 'Do I need a medical exam to get life insurance in Indiana?',
    answer: "It depends on the coverage amount and your age. Many term life insurance policies in Indiana do require a simple medical exam, which we can arrange at your convenience in the Indianapolis area. However, some policies offer simplified underwriting for smaller coverage amounts. We'll help you find the best option for your situation and guide you through every step of the process.",
    articleLink: {
      href: '/blog/best-life-insurance-indianapolis',
      text: 'Read: Best Life Insurance Companies in Indianapolis — No-Exam Options Compared',
    },
  },
  {
    question: 'What is included in a Financial Needs Analysis — and is it really free?',
    answer: "Yes, our Financial Needs Analysis (FNA) is 100% complimentary with no obligation. It's a personalized, one-on-one review designed to give Indianapolis families a clear picture of where they stand financially and what steps to take next. Here's exactly what's covered:\n\n• Income Protection Review — We calculate how much your family would need to maintain their lifestyle if you were no longer there to provide.\n• Debt & Mortgage Assessment — We look at outstanding debts, your mortgage balance, and how life insurance can eliminate that burden for your loved ones.\n• Education Funding — We estimate future college costs for your children and identify strategies to fund them.\n• Retirement Readiness Check — We review whether your current savings plan is on track, and where annuities or whole life insurance could fill the gaps.\n• Life Insurance Gap Analysis — We compare what coverage you currently have (if any) against what your family actually needs.\n• Customized Action Plan — You leave with a simple, written roadmap — no jargon, no pressure — just clear next steps tailored to your Central Indiana family.\n\nThe entire session takes about 45–60 minutes and can be done in person, by phone, or virtually. Most families tell us it's the first time anyone has ever explained their finances in plain English.",
    articleLink: {
      href: '/blog/life-insurance-for-families-indianapolis',
      text: 'See also: Best Life Insurance for Indianapolis Families — Costs & Coverage by Life Stage',
    },
  },
];

export default function Educational() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-16 md:py-[100px] bg-white">
      <div className="max-w-[1000px] mx-auto px-4 sm:px-8">
        <div className="text-center mb-10 md:mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#F7F9FB] text-[#1A3A52] text-[13px] font-medium uppercase tracking-wide rounded-full border border-gray-200">
            Common Questions
          </span>
          <h2 className="text-[26px] sm:text-[32px] md:text-[38px] font-bold text-[#1A3A52] mt-5 md:mt-7">
            Life Insurance Guidance for Central Indiana
          </h2>
        </div>

        <div className="space-y-4 md:space-y-5">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-[#F9FAFB] rounded-xl md:rounded-2xl overflow-hidden border border-gray-200 hover:border-[#D97642] transition-colors"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-5 md:px-7 py-5 md:py-6 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <span className="text-[15px] md:text-[18px] font-medium text-[#1A3A52] pr-3 md:pr-4">
                  {faq.question}
                </span>
                <i className={`ri-arrow-down-s-line text-[22px] md:text-[24px] text-[#D97642] transition-transform flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`}></i>
              </button>
              {openIndex === index && (
                <div className="px-5 md:px-7 pb-5 md:pb-7">
                  <p className="text-[14px] md:text-[16px] text-[#4A5568] leading-[1.7] whitespace-pre-line">
                    {faq.answer}
                  </p>
                  {faq.articleLink && (
                    <Link
                      to={faq.articleLink.href}
                      className="inline-flex items-center gap-2 mt-4 text-[13px] font-medium text-[#D97642] hover:text-[#C26638] transition-colors group/link cursor-pointer"
                    >
                      <i className="ri-article-line text-[15px] flex-shrink-0"></i>
                      <span className="underline underline-offset-2 decoration-[#D97642]/40 group-hover/link:decoration-[#C26638]">
                        {faq.articleLink.text}
                      </span>
                      <i className="ri-arrow-right-line text-[13px] group-hover/link:translate-x-0.5 transition-transform flex-shrink-0"></i>
                    </Link>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Learning Hub Callout */}
        <Link
          to="/learning-hub"
          className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#1A3A52] rounded-2xl px-6 md:px-8 py-5 md:py-6 hover:bg-[#1e4666] transition-colors group cursor-pointer"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#D97642]/20 flex-shrink-0">
              <i className="ri-book-open-line text-[22px] text-[#D97642]"></i>
            </div>
            <div className="text-left">
              <p className="text-[15px] md:text-[16px] font-semibold text-white leading-snug">
                Life Insurance Learning Hub
              </p>
              <p className="text-[13px] text-[#A0B8CC] mt-0.5">
                27 Indiana guides + 7 city breakdowns — all in one place
              </p>
            </div>
          </div>
          <span className="flex items-center gap-1.5 text-[#D97642] text-[14px] font-semibold whitespace-nowrap group-hover:translate-x-1 transition-transform">
            Browse all guides <i className="ri-arrow-right-line text-[16px]"></i>
          </span>
        </Link>

        <div className="mt-8 md:mt-10 text-center">
          <p className="text-[16px] md:text-[18px] text-[#4A5568] mb-5 md:mb-6">
            Have more questions about life insurance in Indianapolis?
          </p>
          <a
            href="tel:+13176482983"
            className="inline-flex items-center gap-2 px-6 md:px-8 py-3.5 md:py-4 bg-[#D97642] text-white text-[15px] md:text-[16px] font-medium rounded-full hover:bg-[#C26638] transition-all hover:shadow-lg cursor-pointer"
          >
            <i className="ri-phone-fill text-[18px] md:text-[20px]"></i>
            Call Us Today: (317) 648-2983
          </a>
        </div>
      </div>
    </section>
  );
}